import React, { useContext, useState, useCallback, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { MultiSelect } from 'react-multi-select-component';
import { ActionComponent } from './ActionComponent';
// eslint-disable-next-line import/no-cycle
import { Context } from './ContextWrapper';
import * as action from '../Store/SystemMasters.action';
import ConfirmationPopup from '../../../Components/ModalPopup/ConfirmationPopup';
import * as userAction from '../../UserSetting/store/User.action';
import { checkArray, createOptions } from '../../../Components/helper';
import StatusSettings from './StatusSettings';
import CommonMultiSelect from '../../../Components/Utility/FilterAction/CommonMultiSelect';

function CardItem({ index, item: cardDetails, cards }) {
  const { _id, name, isActive } = cardDetails;
  const {
    updateArray,
    removeFromArray,
    formState,
    setFormValues,
    setFormState,
    MasterDeleteAccess,
    MasterAddEditAccess,
    documentMasterTypes,
    setProductType,
    onDropSelectionChange,
    setStatus,
    statusCards,
    setStatusCards,
  } = useContext(Context);
  const {
    UserDetails: { userInfo },
    SystemMastersDetails: { pcTypeAdminList, supplierStatusList },
  } = useSelector((state) => state);
  const genId = _id || `_id${index}`;
  const dispatch = useDispatch();
  const { t } = useTranslation('common');

  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const productTypeText = 'PRODUCT_COMPONENT_TYPE.PRODUCT_TYPE';
  const [shouldShowDelete, setShouldShowDelete] = useState(true);
  const [shouldShowConfirmation, setShouldShowConfirmation] = useState(true);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!Object.keys(formState?.values)?.includes(_id)) {
      setFormValues(name, _id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_id, name, formState]);

  useEffect(() => {
    if (genId.includes('_id')) {
      setShouldShowDelete(false);
      setShouldShowConfirmation(false);
    } else {
      setShouldShowConfirmation(true);
      setShouldShowDelete(true);
    }
  }, [genId]);

  const handleBlur = (e) => {
    const { value } = e.target;
    updateArray({ name: value }, genId);
  };

  const handleDelete = useCallback(async () => {
    try {
      if (genId?.includes('_id')) {
        removeFromArray(genId);

        setFormValues('', genId);
        return null;
      }

      const obj = {
        loggedInUserId: userInfo.userId,
        id: genId,
      };
      setIsLoading(true);
      if (documentMasterTypes?.value === 'specification') {
        const req = { section: 'masters', module: 'documentType', action: 'delete' };
        await dispatch(action.deleteDocumentType(obj, t, req));
        await dispatch(action.fetchDocumentType({}, t));
        setIsLoading(false);
        setShowDelete((prev) => !prev);
      } else if (documentMasterTypes?.value === 'manual') {
        const req = { section: 'masters', module: 'documentType', action: 'delete' };
        await dispatch(action.deleteManualDocumentType(obj, t, req));
        await dispatch(action.fetchManualDocumentType({}, t));
        setIsLoading(false);
        setShowDelete((prev) => !prev);
      } else {
        const objData = {
          docId: genId,
        };
        dispatch(userAction.deleteUserDocument(objData, t));
        dispatch(userAction.fetchUserDocumentsList({ fromMaster: 'true' }));
        setIsLoading(false);
        setShowDelete((prev) => !prev);
      }
    } catch (error) {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, genId, documentMasterTypes, removeFromArray, setFormValues, t, userInfo.userId, setFormState]);

  const handleActiveInactive = useCallback(async () => {
    try {
      if (genId?.includes('_id')) {
        return null;
      }
      if (documentMasterTypes?.value === 'specification') {
        const obj = {
          ...cardDetails,
          isActive: !isActive,
          title: t('DOCUMENT_TYPE.DOCUMENT_TYPES'),
          loggedInUserId: userInfo.userId,
          section: 'masters',
          module: 'documentType',
          action: 'addEdit',
        };
        setIsLoading(true);
        await dispatch(action.setActiveInactive(obj, t));
        await dispatch(action.fetchDocumentType({}, t));
        setIsLoading(false);
        setShowConfirmation((prev) => !prev);
      } else if (documentMasterTypes?.value === 'manual') {
        const obj = {
          ...cardDetails,
          isActive: !isActive,
          title: 'manual-document-type',
          loggedInUserId: userInfo.userId,
          section: 'masters',
          module: 'documentType',
          action: 'addEdit',
        };
        setIsLoading(true);
        await dispatch(action.setActiveInactive(obj, t));
        await dispatch(action.fetchManualDocumentType({}, t));
        setIsLoading(false);
        setShowConfirmation((prev) => !prev);
      } else {
        setIsLoading(true);
        const updatedItems = cards.map((el) => (el._id === cardDetails._id ? { ...el, isActive: !isActive } : el));
        await dispatch(userAction.addUserDocumentsList(updatedItems, t, 'active'));
        await dispatch(userAction.fetchUserDocumentsList({ fromMaster: 'true' }));
        setIsLoading(false);
        setShowConfirmation((prev) => !prev);
      }
    } catch (error) {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardDetails, dispatch, genId, isActive, t, userInfo.userId]);

  const handleDropdownChange = (value, type) => {
    if (type === 'productType') {
      setProductType(value);
      updateArray({ productType: value?.map((it) => it._id) }, genId);
    } else {
      setStatus(value);
      updateArray({ status: value?.map((it) => it._id) }, genId);
    }
  };
  const supplierOptions = useMemo(() => {
    if (checkArray(supplierStatusList)) {
      const activeStatus = supplierStatusList?.filter((el) => el.docId === cardDetails?._id && el?.isActive === true);
      return createOptions(activeStatus, 'name', '_id');
    }
    return [];
  }, [supplierStatusList]);

  const supplierOptionsList = useMemo(() => {
    if (checkArray(supplierStatusList)) {
      const activeStatus = supplierStatusList?.filter((el) => el.docId === cardDetails?._id);
      return createOptions(activeStatus, 'name', '_id');
    }
    return [];
  }, [supplierStatusList]);

  const deleteAction = shouldShowDelete ? () => setShowDelete((prev) => !prev) : handleDelete;
  const activeAction = () => setShowConfirmation((prev) => !prev);
  const settingAction = () => {
    if (supplierOptionsList.length > 0) {
      setStatusCards(supplierOptionsList);
    } else {
      setStatusCards((prev) => [...prev, { _id: '', name: '' }]);
    }
    setShowSettings((prev) => !prev);
  };

  const productTypeOptions = useMemo(() => {
    if (checkArray(pcTypeAdminList)) return createOptions(pcTypeAdminList, 'name', '_id');
    return [];
  }, [pcTypeAdminList]);

  const handleClose = () => {
    setShowSettings((prev) => !prev);
    setStatusCards([]);
  };
  const handleAdd = useCallback(() => {
    setStatusCards((prev) => [...prev, { _id: '', name: '' }]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <>
      <div className="list-container">
        <li>
          <div className="input-container">
            <div className="d-flex">
              <input
                key={index}
                name={genId}
                value={formState?.values?.[genId]}
                onChange={(e) => {
                  const inputValue = e.target.value.trimStart().slice(0, 50);
                  setFormValues(inputValue, e.target.name);
                }}
                placeholder={t('DOCUMENT_TYPE.ENTER_DOCUMENT_TYPE')}
                type="text"
                onBlur={handleBlur}
                disabled={!MasterAddEditAccess}
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus={_id?.includes('_id')}
              />
            </div>
            {formState.errors?.[genId] && <p className="text-danger m-0 p-1">{formState.errors?.[genId]}</p>}
          </div>

          <div className="input-action">
            <ActionComponent
              deleteAction={deleteAction}
              activeAction={activeAction}
              showActiveBtn={shouldShowConfirmation}
              isActive={isActive}
              MasterDeleteAccess={MasterDeleteAccess}
              MasterAddEditAccess={MasterAddEditAccess}
              documentMasterTypes={documentMasterTypes}
              settingAction={settingAction}
              cardDetails={cardDetails}
              t={t}
            />
          </div>
        </li>
      </div>
      {documentMasterTypes.value === 'supplier' && (
        <div className="p-3 m-3 status-doc-type">
          <div className="select-container px-0">
            <MultiSelect
              options={productTypeOptions}
              placeholder={t(productTypeText)}
              getOptionLabel={(option) => option.name}
              getOptionValue={(option) => option.value}
              key={index}
              name="productType"
              labelledBy={t(productTypeText)}
              overrideStrings={{
                selectSomeItems: t(productTypeText), // Adding an asterisk to the placeholder text
                search: t(productTypeText),
              }}
              isMulti
              className="custom-multiselect product-type-multiselect li-padding-zero"
              value={productTypeOptions?.filter((option) => cardDetails?.productType?.includes(option._id))}
              onChange={(selectedOptions) => handleDropdownChange(selectedOptions, 'productType')}
            />
            {formState?.isSubmitted && (cardDetails?.productType === null || cardDetails?.productType === undefined || !cardDetails.productType) && (
              <label htmlFor="error" className="text-danger m-0 p-1">
                {t('DROP_DOWN_CARD.PRODUCT_TYPE_REQUIRED')}
              </label>
            )}
          </div>
          <div className="select-container px-0">
            <CommonMultiSelect
              dataPlaceholder={t('SUPLIER.ADD_EDIT_SUPPLIER_OVERVIEW.SELECT_STATUS')}
              onChange={(e) => onDropSelectionChange(e, index)}
              selectedValue={cardDetails?.status}
              optionList={cardDetails?._id ? supplierOptions : supplierOptions?.filter((e) => e?.isActive) || []}
              selectLabel="name"
              selectValue="_id"
            />
          </div>
        </div>
      )}
      {showSettings && (
        <StatusSettings
          docId={cardDetails?._id}
          statusCards={statusCards}
          setStatusCards={setStatusCards}
          onOpen={showSettings}
          title={t('DOCUMENT_TYPE.SETTINGS')}
          handleClose={handleClose}
          handleAdd={handleAdd}
        />
      )}

      {shouldShowDelete && (
        <ConfirmationPopup
          onOpen={showDelete}
          onConfirm={() => handleDelete()}
          title={t('DOCUMENT_TYPE.DELETE_TITLE')}
          message={t('DOCUMENT_TYPE.DELETE_MESSAGE')}
          toggle={() => {
            setShowDelete((prev) => !prev);
          }}
          loading={isLoading}
        />
      )}

      <ConfirmationPopup
        onOpen={showConfirmation}
        onConfirm={() => handleActiveInactive()}
        title={t('DOCUMENT_TYPE.ACTIVE/DEACTIVE')}
        message={t('DOCUMENT_TYPE.CONFIRMATION_MESSAGE')}
        toggle={() => {
          setShowConfirmation((prev) => !prev);
        }}
        loading={isLoading}
      />
    </>
  );
}

export default CardItem;
